import { useEffect, useState } from "react";
import {
  ChevronDown, HardHat, Image as ImageIcon, Wallet, ShieldCheck, LifeBuoy,
  Gift, LogOut, ArrowLeftRight, Phone, MessageCircle, Bell,
} from "lucide-react";

import { initials } from "../lib/fmt";
import { Mark } from "../brand/Mark";
import { VeehaDisc } from "../brand/Veeha";
import { useCare } from "./Care";

/**
 * Drawer — the account panel, from reference screen 1.
 *
 * The Care badge at the top is the direct analogue of that screen's security
 * badge: one line, always visible, states whether the cover is live. It is the
 * single most reassuring thing the portal can show a client who has just taken
 * delivery of a home, so it sits above every navigation item rather than
 * inside the Care section.
 */

const SECTIONS = [
  {
    key: "project",
    icon: HardHat,
    label: "My project",
    note: "Schedule, tasks, site log, finishes",
    kids: [["project", "Milestones & schedule"], ["project", "Site visit log"], ["project", "Finish schedule"]],
  },
  {
    key: "designs",
    icon: ImageIcon,
    label: "Designs & ideas",
    note: "Your renders and the idea gallery",
    kids: [["designs", "Your designs"], ["designs", "Idea gallery"]],
  },
  {
    key: "payments",
    icon: Wallet,
    label: "Payments & invoices",
    note: "Balance, invoices, receipts",
    kids: [["payments", "Invoices"], ["payments", "Receipts"], ["payments", "How to pay"]],
  },
  {
    key: "care",
    icon: ShieldCheck,
    label: "D'leaps Care",
    note: "Cover, free service visits, requests",
    kids: [["care", "My cover"], ["care", "Raise a service request"]],
  },
  {
    key: "refer",
    icon: Gift,
    label: "Refer & earn",
    note: "₹25,000 for every friend who signs",
    kids: [["refer", "Introduce someone"], ["refer", "Your referrals"]],
  },
  {
    key: "support",
    icon: LifeBuoy,
    label: "Help & requests",
    note: "Talk to your project team",
    kids: [["support", "Raise a request"], ["updates", "Updates feed"]],
  },
];

export function Drawer({ ctx, onClose, onPick, onAsk }) {
  const { clientName, project, projects, studio } = ctx;
  const [open, setOpen] = useState(null);
  const { card } = useCare();

  /* Escape closes it, and the page behind stops scrolling while it is open —
     without that, a scroll gesture that starts on the drawer and continues
     past its end scrolls Home underneath, which reads as a broken panel. */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const careOn = !!card?.active;
  const careWord = card?.needs_end_date ? "PENDING"
    : card?.expired ? "ENDED"
      : card?.not_started ? "FROM HANDOVER"
        : careOn ? "ACTIVE" : "PENDING";

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label="Your account">
        <div className="dr-top">
          <span className="dr-avatar" aria-hidden="true">
            <span style={{ fontFamily: "var(--font-bold)", fontWeight: 700, color: "#fff", fontSize: ".95rem" }}>
              {initials(clientName)}
            </span>
          </span>
          <span className="dr-name">{clientName || "Your account"}</span>
          <span className="dr-meta">
            {project?.title}
            {project?.project_code ? ` · ${project.project_code}` : ""}
          </span>
          {projects?.length > 1 && (
            <button className="dr-link" onClick={() => onPick("switch")}>
              <ArrowLeftRight size={13} /> Switch project
            </button>
          )}
        </div>

        {/* the Care badge */}
        <button className="dr-care" onClick={() => onPick("care")}>
          <Mark size={22} className="mark" />
          <b>D&rsquo;leaps Care</b>
          <span className={"state " + (careOn ? "on" : "off")}>{careWord}</span>
        </button>

        <button
          className="dr-care"
          style={{ marginTop: -8 }}
          onClick={onAsk}
          aria-label="Ask Veeha, D'leaps AI"
        >
          <VeehaDisc size={26} />
          <b>Ask Veeha</b>
          <span className="state off">D&rsquo;LEAPS AI</span>
        </button>

        {SECTIONS.map((s) => {
          const isOpen = open === s.key;
          const Icon = s.icon;
          return (
            <div className={"dr-sec" + (isOpen ? " open" : "")} key={s.key}>
              <button onClick={() => setOpen(isOpen ? null : s.key)} aria-expanded={isOpen}>
                <span className="dr-ic"><Icon size={20} strokeWidth={1.5} /></span>
                <span className="dr-lbl">
                  <b>{s.label}</b>
                  <small>{s.note}</small>
                </span>
                <ChevronDown size={17} className="chev" />
              </button>
              {isOpen && (
                <div className="dr-kids">
                  {s.kids.map(([target, label]) => (
                    <button key={label} onClick={() => onPick(target)}>{label}</button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="dr-sec">
          <button onClick={() => onPick("updates")}>
            <span className="dr-ic"><Bell size={20} strokeWidth={1.5} /></span>
            <span className="dr-lbl"><b>Updates</b><small>Everything that happened, newest first</small></span>
          </button>
        </div>

        {studio?.phone && (
          <div className="dr-sec">
            <a className="dr-sec-a" href={`tel:${studio.phone}`} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 20px" }}>
              <span className="dr-ic"><Phone size={20} strokeWidth={1.5} /></span>
              <span className="dr-lbl"><b>Call the studio</b><small className="mono">{studio.phone}</small></span>
            </a>
          </div>
        )}

        {card?.whatsapp && (
          <div className="dr-sec">
            <a href={card.whatsapp} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", color: "inherit" }}>
              <span className="dr-ic"><MessageCircle size={20} strokeWidth={1.5} /></span>
              <span className="dr-lbl"><b>WhatsApp us</b><small>Usually replies within the hour</small></span>
            </a>
          </div>
        )}

        <div className="dr-sec">
          <button onClick={ctx.signOut}>
            <span className="dr-ic"><LogOut size={20} strokeWidth={1.5} /></span>
            <span className="dr-lbl"><b>Sign out</b></span>
          </button>
        </div>

        <p className="muted" style={{ padding: "18px 20px calc(var(--safe-b) + 28px)", fontSize: ".72rem" }}>
          {studio?.name || "D'leaps Interior Design Studio"}
          {studio?.address ? ` · ${studio.address}` : ""}
        </p>
      </aside>
    </>
  );
}
